"use client";

// UPDATED: 2025-10-09 - Updated to use NextAuth for secure authentication
import * as React from "react";
import { useState, useEffect } from "react";
import { signIn } from "next-auth/react";

// Keep UI minimal to avoid external deps; page stays German-only as required
// This form accepts either an email OR a username like "yazan"
export default function AdminLoginPage() {
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [twoFactorCode, setTwoFactorCode] = useState("");
  const [needs2FA, setNeeds2FA] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function isValidIdentifier(input: string) {
    const v = input.trim();
    if (!v) return false;
    // قبول أي اسم مستخدم بطول 3 أحرف أو أكثر (مثل "yazan")
    return v.length >= 3;
  }

  async function check2FAStatus(identifier: string): Promise<boolean> {
    try {
      // Check if user has 2FA enabled before attempting login
      const response = await fetch('/api/auth/check-2fa', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ identifier: identifier.trim() })
      });
      const data = await response.json();
      return data.has2FA === true;
    } catch {
      return false;
    }
  }

  async function loginWith(id: string, pass: string, twoFactorCode?: string) {
    const callbackUrl = new URL(window.location.href).searchParams.get("next") || "/admin";

    // If no 2FA code provided yet, check if user has 2FA enabled
    if (!twoFactorCode && !needs2FA) {
      const has2FA = await check2FAStatus(id);
      if (has2FA) {
        setNeeds2FA(true);
        throw new Error("الرجاء إدخال رمز المصادقة الثنائية");
      }
    }

    const result = await signIn("credentials", {
      identifier: id.trim(),
      password: pass.trim(),
      twoFactorCode: twoFactorCode?.trim() || "",
      redirect: false,
      callbackUrl,
    });

    if (result?.ok) {
      return result;
    }

    // If 2FA code was provided but login still failed
    if (twoFactorCode) {
      throw new Error("رمز المصادقة الثنائية غير صحيح");
    }

    // Otherwise it's wrong credentials
    throw new Error("بيانات تسجيل الدخول غير صحيحة");
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    e.stopPropagation();
    setError(null);

    if (!isValidIdentifier(identifier)) {
      setError("الرجاء إدخال اسم مستخدم صحيح (3 أحرف على الأقل).");
      return;
    }
    if (password.trim().length < 6) {
      setError("يجب أن تكون كلمة المرور 6 أحرف على الأقل.");
      return;
    }

    // If 2FA is needed but code not provided
    if (needs2FA && !twoFactorCode.trim()) {
      setError("الرجاء إدخال رمز المصادقة الثنائية من تطبيق Google Authenticator");
      return;
    }

    setSubmitting(true);
    try {
      const result = await loginWith(identifier, password, twoFactorCode || undefined);
      if (result?.ok) {
        // Force a full page reload to ensure cookies are properly set
        const next = new URL(window.location.href).searchParams.get("next") || "/admin";
        // Wait a tiny bit for cookie to be set, then do a hard redirect
        await new Promise(resolve => setTimeout(resolve, 100));
        window.location.replace(next);
      }
    } catch (err: any) {
      setError(err.message || "فشل في تسجيل الدخول.");
    } finally {
      setSubmitting(false);
    }
  }

  useEffect(() => {
    try {
      const url = new URL(window.location.href);
      const id = url.searchParams.get("identifier");
      const pass = url.searchParams.get("password");
      const next = url.searchParams.get("next") || "/";
      
      // If credentials are in URL, clean the URL first (security)
      if (id || pass) {
        const cleanUrl = window.location.pathname + (next !== "/" ? `?next=${encodeURIComponent(next)}` : "");
        window.history.replaceState({}, document.title, cleanUrl);
      }
      
      if (id) setIdentifier(id);
      if (pass) setPassword(pass);
      if (id && pass) {
        (async () => {
          setSubmitting(true);
          setError(null);
          try {
            await loginWith(id, pass);
            window.location.href = next;
          } catch (e: any) {
            setError(e?.message || "فشل في تسجيل الدخول.");
          } finally {
            setSubmitting(false);
          }
        })();
      }
    } catch {
      // ignore URL parsing errors
    }
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8" dir="rtl">
      <div className="max-w-md w-full space-y-8">
        <div className="bg-white py-8 px-6 shadow-xl rounded-lg sm:px-10">
          <form onSubmit={onSubmit} method="POST" action="#" className="space-y-6">
            <div className="text-center">
              <h1 className="text-3xl font-bold text-gray-900 mb-2">تسجيل دخول الإدارة</h1>
              <p className="text-gray-600">
                سجل الدخول باستخدام <strong>اسم المستخدم</strong>.
              </p>
            </div>

            <div>
              <label htmlFor="identifier" className="block text-sm font-medium text-gray-700 mb-2">
                اسم المستخدم
              </label>
              <input
                id="identifier"
                name="identifier"
                type="text"
                autoComplete="username"
                required
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                className="appearance-none relative block w-full px-3 py-3 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-lg focus:outline-none focus:ring-[#139195] focus:border-[#139195] focus:z-10 sm:text-sm"
                placeholder="أدخل اسم المستخدم"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                كلمة المرور
              </label>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="appearance-none relative block w-full px-3 py-3 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-lg focus:outline-none focus:ring-[#139195] focus:border-[#139195] focus:z-10 sm:text-sm"
                placeholder="أدخل كلمة المرور"
              />
            </div>

            {needs2FA && (
              <div>
                <label htmlFor="twoFactorCode" className="block text-sm font-medium text-gray-700 mb-2">
                  🔐 رمز المصادقة الثنائية (2FA)
                </label>
                <input
                  id="twoFactorCode"
                  name="twoFactorCode"
                  type="text"
                  required
                  value={twoFactorCode}
                  onChange={(e) => setTwoFactorCode(e.target.value)}
                  maxLength={6}
                  pattern="[0-9]{6}"
                  className="appearance-none relative block w-full px-3 py-3 border border-blue-300 placeholder-gray-500 text-gray-900 rounded-lg focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm text-center text-2xl tracking-widest font-mono"
                  placeholder="000000"
                  autoComplete="off"
                />
                <p className="mt-2 text-sm text-gray-600">
                  أدخل الرمز المكون من 6 أرقام من تطبيق Google Authenticator
                </p>
              </div>
            )}

            {error && (
              <div role="alert" className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm">
                {error}
              </div>
            )}

            <div>
              <button
                type="submit"
                disabled={submitting}
                className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-[#139195] hover:bg-[#0f7a7d] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#139195] disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
              >
                {submitting ? "جارٍ تسجيل الدخول..." : "تسجيل الدخول"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}